/**
 * conversation-settings-lib.ts — Hàm thuần cho màn Cài đặt hội thoại (tự động phân bổ).
 */
import { i18n } from '@/lib/i18n'

export const LIMIT_MAX = 999

/**
 * Ô giới hạn từng nhân viên (chuỗi người dùng gõ) → memberLimits gửi API.
 * Ô trống = theo giới hạn mặc định (không lưu). "0" = không nhận phân bổ tự động.
 * Giá trị bậy bị bỏ qua; vượt trần thì kẹp về LIMIT_MAX.
 */
export function buildMemberLimits(inputs: Record<string, string>): Record<string, number> {
  const out: Record<string, number> = {}
  for (const [userId, raw] of Object.entries(inputs)) {
    const s = raw.trim()
    if (s === '') continue
    const n = Number(s)
    if (!Number.isFinite(n) || n < 0) continue
    out[userId] = Math.min(LIMIT_MAX, Math.round(n))
  }
  return out
}

/** memberLimits từ API → chuỗi cho ô nhập. */
export function limitsToInputs(limits: Record<string, number>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(limits)) out[k] = String(v)
  return out
}

/** "3/20", "Không nhận", "20/20 · đầy". */
export function loadText(openCount: number, limit: number): { text: string; full: boolean } {
  if (limit <= 0) return { text: i18n.t('settings:conversation.notAccepting'), full: false }
  return { text: `${openCount}/${limit}`, full: openCount >= limit }
}

/** Mốc gợi ý "thu hồi nếu không trả lời" (phút). Backend nhận 1–1440; mốc ngoài danh sách (đã lưu từ trước, vd 5 phút, hoặc đặt qua API) vẫn hiện đúng. */
export const RECLAIM_PRESETS = [10, 15, 30, 60, 120, 240] as const

/** "Tắt", "30 phút", "1 giờ", "1 giờ 30 phút". */
export function reclaimLabel(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) return i18n.t('settings:conversation.reclaim.off')
  if (minutes < 60) return i18n.t('settings:conversation.reclaim.minutes', { count: minutes })
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m === 0
    ? i18n.t('settings:conversation.reclaim.hours', { count: h })
    : i18n.t('settings:conversation.reclaim.hoursMinutes', { h, m })
}

/** Các lựa chọn cho ô chọn: Tắt + mốc gợi ý (+ giá trị hiện tại nếu lệch mốc), tăng dần. Giá trị: chuỗi số phút, '0' = tắt (Radix Select không nhận giá trị rỗng). */
export function reclaimOptions(current: number | null | undefined): { value: string; label: string }[] {
  const set = new Set<number>(RECLAIM_PRESETS)
  if (current && current > 0) set.add(current)
  return [
    { value: '0', label: reclaimLabel(null) },
    ...[...set].sort((a, b) => a - b).map((n) => ({ value: String(n), label: reclaimLabel(n) })),
  ]
}
